"use client";

import { useState, useEffect, useCallback } from 'react';
import type { Location, Photo, VoyageState, Hazard, ShipPosition } from '@/types';
import { MAP_BOUNDS, SHIP_SPEED, TURN_ANGLE, HAZARDS as INITIAL_HAZARDS, HAZARD_PROXIMITY_THRESHOLD, LOCATIONS as INITIAL_LOCATIONS } from '@/lib/game-data';

const VOYAGE_STORAGE_KEY = 'konstantins-voyage';

const getInitialState = (): VoyageState => ({
  regionName: "The New England coastline",
  shipPosition: { x: 55, y: 60 },
  shipRotation: 0,
  photos: [],
  mapBackgroundUrl: undefined,
  collision: null,
  locations: INITIAL_LOCATIONS,
  hazards: INITIAL_HAZARDS,
});

export const useVoyageStore = () => {
  const [state, setState] = useState<VoyageState>(getInitialState());
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    try {
      const savedStateJSON = localStorage.getItem(VOYAGE_STORAGE_KEY);
      if (savedStateJSON) {
        const savedState = JSON.parse(savedStateJSON);
        // Ensure certain states are not persisted across page loads
        delete savedState.collision;
        delete savedState.mapBackgroundUrl;
        
        // Merge saved state with a potentially updated initial state structure
        const initialState = getInitialState();
        const mergedState = {
            ...initialState,
            ...savedState,
            // Ensure nested objects are handled if they can be missing
            regionName: savedState.regionName || initialState.regionName,
            shipPosition: savedState.shipPosition || initialState.shipPosition,
            photos: savedState.photos || initialState.photos,
            locations: savedState.locations || initialState.locations,
            hazards: savedState.hazards || initialState.hazards,
        };
        setState(mergedState);
      }
    } catch (error) {
      console.error("Failed to load voyage from local storage", error);
      setState(getInitialState());
    } finally {
      setIsInitialized(true);
    }
  }, []);

  useEffect(() => {
    if (isInitialized) {
      try {
        const stateToSave = { ...state };
        //stateToSave.collision = null;
        delete stateToSave.mapBackgroundUrl;
        const stateJSON = JSON.stringify(stateToSave);
        localStorage.setItem(VOYAGE_STORAGE_KEY, stateJSON);
      } catch (error) {
        console.error("Failed to save voyage to local storage", error);
      }
    }
  }, [state, isInitialized]);

  const turnShip = useCallback((direction: 'left' | 'right') => {
    setState(prevState => ({
      ...prevState,
      shipRotation: (prevState.shipRotation + (direction === 'left' ? -TURN_ANGLE : TURN_ANGLE) + 360) % 360,
    }));
  }, []);

  const moveForward = useCallback(() => {
    setState(prevState => {
      if (prevState.collision) {
        return prevState;
      }

      const angleRad = (prevState.shipRotation - 90) * (Math.PI / 180);
      const dx = Math.cos(angleRad) * SHIP_SPEED;
      const dy = Math.sin(angleRad) * SHIP_SPEED;
      
      const newX = prevState.shipPosition.x + dx;
      const newY = prevState.shipPosition.y + dy;

      const newPosition = {
        x: Math.max(MAP_BOUNDS.minX, Math.min(MAP_BOUNDS.maxX, newX)),
        y: Math.max(MAP_BOUNDS.minY, Math.min(MAP_BOUNDS.maxY, newY)),
      };

      for (const hazard of prevState.hazards) {
        const distance = Math.sqrt(
          Math.pow(newPosition.x - hazard.coords.x, 2) +
          Math.pow(newPosition.y - hazard.coords.y, 2)
        );
        if (distance < HAZARD_PROXIMITY_THRESHOLD) {
          return { ...prevState, collision: hazard };
        }
      }

      return {
        ...prevState,
        shipPosition: newPosition,
      };
    });
  }, []);

  const addPhoto = useCallback((location: Location, imageUrl: string) => {
    setState(prevState => {
      const newPhoto: Photo = {
        id: `${location.id}-${Date.now()}`,
        locationId: location.id,
        imageUrl: imageUrl,
        locationName: location.name,
        timestamp: Date.now(),
        hint: location.hint,
      };
      return {
        ...prevState,
        photos: [newPhoto, ...prevState.photos],
      };
    });
  }, []);

  const deletePhoto = useCallback((photoId: string) => {
    setState(prevState => ({
      ...prevState,
      photos: prevState.photos.filter(p => p.id !== photoId),
    }));
  }, []);

  const startNewVoyage = useCallback((data: { regionName: string; locations: Location[]; hazards: Hazard[]; shipPosition: ShipPosition; mapBackgroundUrl: string; }) => {
    setState(prevState => ({
      ...prevState,
      regionName: data.regionName,
      locations: data.locations,
      hazards: data.hazards,
      shipPosition: data.shipPosition,
      mapBackgroundUrl: data.mapBackgroundUrl,
      collision: null,
      shipRotation: 0,
    }));
  }, []);

  const resetVoyage = useCallback(() => {
    setState(prevState => ({
      ...getInitialState(),
      mapBackgroundUrl: prevState.mapBackgroundUrl,
    }));
  }, []);

  const setMapBackgroundUrl = useCallback((url: string) => {
    setState(prevState => ({
        ...prevState,
        mapBackgroundUrl: url,
    }));
  }, []);

  const clearCollision = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      collision: null,
    }));
  }, []);

  return {
    ...state,
    isInitialized,
    turnShip,
    moveForward,
    addPhoto,
    deletePhoto,
    resetVoyage,
    setMapBackgroundUrl,
    clearCollision,
    startNewVoyage,
  };
};
